import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react/function-with-condition/function-with-condition';

import { fg } from '@confluence/feature-gating';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { UFOTransitionMarkFMP } from '@confluence/browser-metrics/entry-points/UFOTransitionMarkFMP.ts';
import { browserMetricsState } from '@confluence/browser-metrics/entry-points/utils.ts';
import { WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { ScrollToPageStart } from '@confluence/scroll/entry-points/ScrollToPageStart.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	TASKS_HOME_LOAD_KEY,
	CONFLUENCE_DRAFTS_LOAD_KEY,
	CONFLUENCE_RECENT_LOAD_KEY,
	CONFLUENCE_STARRED_LOAD_KEY,
} from '@confluence/browser-metrics/entry-points/metric-keys/pageLoadKey.ts';
import { expVal } from '@confluence/feature-experiments';

import { CONFLUENCE_HOME_PAGE_LOAD } from '../perf.config.ts';

import { HomeMainContentArea } from './HomeMainContentArea.tsx';
import { HomeRovoButtonContainer } from './HomeRovoButtonContainer.tsx';
import { Error } from './HomeViews/index.ts';
import { useHomeReadyForUser } from './useHomeReadyForUser.ts';
import { useHomeViewedAnalytics } from './useHomeViewedAnalytics.ts';
import { useTWCG2110LoomCrossflow } from './useTWCG2110LoomCrossflow.ts';
import { useRightSidebarCompensation } from './useRightSidebarCompensation.ts';
import { SINGLE_COLUMN_BREAKPOINT_PX } from './layoutConstants.ts';
import { useSelectedView } from './useSelectedView.ts';
import { View } from './homeRouteParams.ts';

const pageLoadMetricLookup = {
	[View.OVERVIEW]: undefined, //this is the default
	[View.DRAFTS]: CONFLUENCE_DRAFTS_LOAD_KEY,
	[View.RECENT]: CONFLUENCE_RECENT_LOAD_KEY,
	[View.STARRED]: CONFLUENCE_STARRED_LOAD_KEY,
	[View.TASKS]: TASKS_HOME_LOAD_KEY,
};

// Views that render their own visible <h1>, so the screen-reader-only home heading is hidden.
const VIEWS_WITH_OWN_HEADING: ReadonlySet<View> = new Set([
	View.RECENT,
	View.STARRED,
	View.DRAFTS,
	View.TASKS,
]);

const i18n = defineMessages({
	homeH1Text: {
		id: 'home.h1.text',
		defaultMessage: 'Confluence Home',
		description: 'H1 heading text for the home page - visible to screen readers only',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const HomeContainer = styled.div`
	display: flex;
	justify-content: center;
	background-color: ${token('elevation.surface')};
	width: 100%;
	min-height: calc(100vh - var(--bannerHeight, 0px) - var(--topNavigationHeight, 0px));
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const HomeLayout = styled.div<{ rightSidebarCompensation: number }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ rightSidebarCompensation }) => ({
		display: 'flex',
		flexBasis: '100%',
		margin: token('space.400'),
		maxWidth: '1440px',
		minWidth: '400px',
		'--homeIsSingleColumn': 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[`@media (max-width: ${SINGLE_COLUMN_BREAKPOINT_PX + rightSidebarCompensation}px)`]: {
			'--homeIsSingleColumn': 1,
		},
	}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const ContentContainer = styled.div`
	display: flex;
	flex-basis: 100%;
	max-width: 1152px;
	min-width: 516px;
	flex-grow: 1;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScreenReaderOnlyHeading = styled.h1({
	border: token('space.0'),
	clip: 'rect(1px, 1px, 1px, 1px)',
	'clip-path': 'inset(50%)',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	margin: '-1px',
	overflow: 'hidden',
	padding: '0',
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	'word-wrap': 'normal !important',
});

const useTWCG2110LoomCrossflowWithKillswitch = functionWithCondition(
	() => fg('twcg-2110--loom-standalone-crossflow-killswitch'),
	useTWCG2110LoomCrossflow,
	() => {},
);

export const Home = (): React.JSX.Element => {
	const intl = useIntl();
	const homeReadyForUser = useHomeReadyForUser();
	const rightSidebarCompensation = useRightSidebarCompensation();
	const selectedView = useSelectedView();

	const hideHomeHeadingForView = VIEWS_WITH_OWN_HEADING.has(selectedView);

	const { overrideUFORouteName } = useRouteActions();

	useEffect(() => {
		//set ufo page name correctly for each home route
		const pageLoadKey = pageLoadMetricLookup[selectedView];
		if (pageLoadKey) {
			overrideUFORouteName(pageLoadKey);
		}
	}, [overrideUFORouteName, selectedView]);

	useHomeViewedAnalytics();
	useTWCG2110LoomCrossflowWithKillswitch();

	// SSR-only A/A test for the Confluence FY27 H1 holdout. Fired only on the server (render body,
	// not a useEffect) to investigate whether SSR-based exposures cause the holdout's double
	// exposures: https://hello.atlassian.net/wiki/spaces/~6362282ab0b6ef03564afd8e/pages/7432450542
	// Exposure-only guard, renders no markup, so no hydration risk. #twc-holdout-working-group
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		expVal('confluence_aa_test_fy27h1_ssr', 'isEnabled', false);
	}

	useEffect(() => {
		// A/A test for the Confluence FY26 H2 holdout. Contact #twc-holdout-working-group for questions.
		expVal('confluence_aa_test', 'isEnabled', false);
		// A/A test for the Confluence FY27 H1 holdout. Contact #twc-holdout-working-group for questions.
		expVal('confluence_aa_test_fy27h1', 'isEnabled', false);
	}, []);

	return (
		<>
			<ScrollToPageStart isSmooth={false} />
			<ScreenReaderOnlyHeading
				aria-hidden={hideHomeHeadingForView || undefined}
				data-vc="screen-reader-home-h1-text"
			>
				{intl.formatMessage(i18n.homeH1Text)}
			</ScreenReaderOnlyHeading>
			<GenericErrorBoundary attribution={Attribution.DISCO} renderOnError={Error}>
				<HomeContainer data-testid="home" data-vc="home-container">
					<HomeLayout rightSidebarCompensation={rightSidebarCompensation} data-testid="home-layout">
						<ContentContainer>
							<GenericErrorBoundary attribution={Attribution.DISCO} renderOnError={Error}>
								<HomeMainContentArea />
							</GenericErrorBoundary>
						</ContentContainer>
					</HomeLayout>
				</HomeContainer>
				<HomeRovoButtonContainer />
			</GenericErrorBoundary>
			{homeReadyForUser && (
				<>
					<PageLoadEnd
						metric={CONFLUENCE_HOME_PAGE_LOAD}
						customData={{
							initiallyWikiRoute: browserMetricsState.previousRoute === WIKI.name,
						}}
						// Set TTI = FMP ( = FCP) when SSR
						stopTime={window.__SSR_RENDERED__ ? getMark('CFP-63.ssr-ttr') : undefined}
					/>
					<UFOTransitionMarkFMP />
				</>
			)}
		</>
	);
};
